import { FingerprintData } from "@/core/dtos/fingerprint-data";
import { cn } from "@/lib/utils";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Globe,
  Hash,
  Monitor,
  Palette,
  Smartphone,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { Button } from "../ui/button";

interface FingerprintDisplayProps {
  data: FingerprintData[];
  className?: string;
}

const FingerprintDisplay: React.FC<FingerprintDisplayProps> = ({
  data,
  className = "",
}) => {
  const [activeTab, setActiveTab] = useState<"pretty" | "json">("pretty");
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedStates, setCopiedStates] = useState<
    Record<number, "idle" | "copying" | "copied">
  >({});

  const jsonString = useMemo(() => JSON.stringify(data, null, 2), [data]);

  const copyToClipboard = async (
    fingerprintIndex: number,
    content: string,
  ): Promise<void> => {
    setCopiedStates((prev) => ({ ...prev, [fingerprintIndex]: "copying" }));
    try {
      await navigator.clipboard.writeText(content);
      setCopiedStates((prev) => ({ ...prev, [fingerprintIndex]: "copied" }));
      setTimeout(
        () =>
          setCopiedStates((prev) => ({ ...prev, [fingerprintIndex]: "idle" })),
        2000,
      );
    } catch {
      setCopiedStates((prev) => ({ ...prev, [fingerprintIndex]: "idle" }));
    }
  };

  const renderPrettyView = (): React.ReactElement => {
    return (
      <div className="space-y-6">
        {data.map((fingerprint, index) => (
          <div key={index} className="border rounded-lg p-6 bg-card">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6 pb-4 border-b">
              <Hash className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <h3 className="font-semibold text-foreground text-lg">
                  Fingerprint #{index + 1}
                </h3>
                <p className="text-sm text-muted-foreground font-mono truncate max-w-[41ch]">
                  {fingerprint.hash || "N/A"}
                </p>
              </div>
              <Button
                onClick={() =>
                  copyToClipboard(index, JSON.stringify(fingerprint, null, 2))
                }
                disabled={copiedStates[index] === "copying"}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                  "bg-primary text-primary-foreground hover:bg-primary/90 text-sm",
                )}
              >
                {copiedStates[index] === "copied" ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
              {/* Screen Info */}
              {fingerprint.screen && (
                <div className="bg-background rounded-lg p-4 border shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <Monitor className="w-4 h-4 text-chart-2" />
                    <h4 className="font-medium text-foreground">Screen</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Resolution:</span>
                      <span className="font-mono text-foreground">
                        {fingerprint.screen.width}×{fingerprint.screen.height}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Available:</span>
                      <span className="font-mono text-foreground">
                        {fingerprint.screen.availWidth}×
                        {fingerprint.screen.availHeight}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Color Depth:
                      </span>
                      <span className="font-mono text-foreground">
                        {fingerprint.screen.colorDepth}-bit
                      </span>
                    </div>
                    {fingerprint.screen.orientation && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Orientation:
                        </span>
                        <span className="font-mono text-foreground">
                          {fingerprint.screen.orientation.type}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Browser Info */}
              {fingerprint.browser && (
                <div className="bg-background rounded-lg p-4 border shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4 text-primary" />
                    <h4 className="font-medium text-foreground">Browser</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Platform:</span>
                      <span className="font-mono text-foreground">
                        {fingerprint.browser.platform}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Language:</span>
                      <span className="font-mono text-foreground">
                        {fingerprint.browser.language}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Concurrency:
                      </span>
                      <span className="font-mono text-foreground">
                        {fingerprint.browser.hardwareConcurrency}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Vendor:</span>
                      <span className="font-mono text-xs text-foreground truncate max-w-[16ch] block">
                        {fingerprint.browser.vendor || "N/A"}
                      </span>
                    </div>
                    {fingerprint.browser.availableFonts && (
                      <div className="mt-3">
                        <span className="text-muted-foreground text-xs">
                          Fonts ({fingerprint.browser.availableFonts.length}):
                        </span>
                        <div className="mt-1 max-h-20 overflow-y-auto">
                          <div className="flex flex-wrap gap-1">
                            {fingerprint.browser.availableFonts
                              .slice(0, 6)
                              .map((font, i) => (
                                <span
                                  key={i}
                                  className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono text-muted-foreground"
                                >
                                  {font}
                                </span>
                              ))}
                            {fingerprint.browser.availableFonts.length > 6 && (
                              <span className="px-1.5 py-0.5 bg-secondary rounded text-xs text-secondary-foreground">
                                +{fingerprint.browser.availableFonts.length - 6}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Device Info */}
              {fingerprint.device && (
                <div className="bg-background rounded-lg p-4 border shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <Smartphone className="w-4 h-4 text-chart-4" />
                    <h4 className="font-medium text-foreground">Device</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Memory:</span>
                      <span className="font-mono text-foreground">
                        {fingerprint.device.deviceMemory}GB
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Connection:</span>
                      <span className="font-mono text-foreground">
                        {fingerprint.device.connectionEffectiveType}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Downlink:</span>
                      <span className="font-mono text-foreground">
                        {fingerprint.device.connectionDownlink}Mbps
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">RTT:</span>
                      <span className="font-mono text-foreground">
                        {fingerprint.device.connectionRtt}ms
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Timezone Info */}
              {fingerprint.timezone && (
                <div className="bg-background rounded-lg p-4 border shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4 text-chart-1" />
                    <h4 className="font-medium text-foreground">Timezone</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Zone:</span>
                      <span className="font-mono text-foreground">
                        {fingerprint.timezone.timezone}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Locale:</span>
                      <span className="font-mono text-foreground">
                        {fingerprint.timezone.locale}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Offset:</span>
                      <span className="font-mono text-foreground">
                        {fingerprint.timezone.timezoneOffset}min
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Canvas/Audio Info */}
              {(fingerprint.canvas || fingerprint.audio) && (
                <div className="bg-background rounded-lg p-4 border shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <Palette className="w-4 h-4 text-chart-5" />
                    <h4 className="font-medium text-foreground">
                      Fingerprints
                    </h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    {fingerprint.audio?.audioFingerprint && (
                      <div>
                        <span className="text-muted-foreground text-xs">
                          Audio:
                        </span>
                        <p className="font-mono text-xs mt-1 p-2 bg-muted rounded text-muted-foreground">
                          {fingerprint.audio.audioFingerprint}
                        </p>
                      </div>
                    )}
                    {fingerprint.canvas?.canvasFingerprint && (
                      <div>
                        <span className="text-muted-foreground text-xs">
                          Canvas:
                        </span>
                        <p className="font-mono text-xs mt-1 p-2 bg-muted rounded text-muted-foreground">
                          <span className="max-w-[50ch] break-all overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                            {fingerprint.canvas.canvasFingerprint || "N/A"}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderJsonView = (): React.ReactElement => {
    return (
      <div className="relative">
        <pre className="bg-muted text-muted-foreground p-6 rounded-lg overflow-auto text-sm font-mono leading-relaxed border">
          <code
            dangerouslySetInnerHTML={{
              __html: syntaxHighlightJson(jsonString),
            }}
          />
        </pre>
      </div>
    );
  };

  const syntaxHighlightJson = (json: string): string => {
    // Escape HTML before adding highlight markup so attacker-controlled values
    // in the fingerprint data (e.g. a userAgent containing `<img onerror=...>`)
    // render as inert text instead of executing. Order matters: escape `&`
    // first. `"` is intentionally left unescaped so the quote-matching regexes
    // below still work — safe because the output is placed as element content
    // inside <code>, not in an attribute.
    return json
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/("[\w]+"):/g, '<span class="text-primary">$1</span>:')
      .replace(/: (".*?")/g, ': <span class="text-chart-2">$1</span>')
      .replace(/: ([\d.]+)/g, ': <span class="text-chart-1">$1</span>')
      .replace(/: (true|false|null)/g, ': <span class="text-chart-4">$1</span>')
      .replace(/{/g, '<span class="text-muted-foreground">{</span>')
      .replace(/}/g, '<span class="text-muted-foreground">}</span>')
      .replace(/\[/g, '<span class="text-muted-foreground">[</span>')
      .replace(/\]/g, '<span class="text-muted-foreground">]</span>');
  };

  return (
    <div
      className={cn(
        "bg-card border rounded-lg shadow-lg overflow-hidden",
        className,
      )}
    >
      {/* Header */}
      <div className="border-b bg-muted/50">
        <div className="flex items-center justify-between p-6">
          <h2 className="text-xl font-semibold text-foreground">
            Device Fingerprints ({data.length})
          </h2>
          <div className="flex bg-background rounded-md border shadow-sm">
            <button
              onClick={() => setActiveTab("pretty")}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-l-md transition-colors",
                activeTab === "pretty"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted",
              )}
            >
              Pretty
            </button>
            <button
              onClick={() => setActiveTab("json")}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-r-md transition-colors",
                activeTab === "json"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted",
              )}
            >
              JSON
            </button>
          </div>

          {/* Removed buttons from header - will be moved to appropriate locations */}
        </div>
      </div>

      {/* Content */}
      <div className="relative">
        <div
          className={cn(
            "transition-all duration-500 ease-in-out overflow-hidden",
            isExpanded ? "max-h-none" : "max-h-96",
          )}
        >
          <div className="p-6">
            {activeTab === "pretty" ? renderPrettyView() : renderJsonView()}
          </div>
        </div>

        {/* Gradient overlay when collapsed */}
        {!isExpanded && (
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-card via-card/80 to-transparent pointer-events-none" />
        )}
      </div>

      {/* Expand/Collapse button at bottom */}
      <div className="border-t bg-muted/50 p-4 flex justify-center">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors",
            "hover:bg-muted text-muted-foreground hover:text-foreground bg-background",
          )}
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Collapse
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Expand
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FingerprintDisplay;
